import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { requireSessionUser, requireTenantUser } from '@/integrations/supabase/session';
import { toast } from 'sonner';
import { sanitizeSearchQuery } from '@/utils/sanitize';
import { normalizePhoneE164 } from '@/utils/phone';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { Customer } from '@/types';

const CUSTOMER_SELECT_COLUMNS =
  'id, company_id, created_by, customer_name, customer_number, customer_since, customer_type, company_name, primary_phone, secondary_phone, normalized_phone, primary_email, secondary_email, normalized_email, primary_address, secondary_address, suburb, state, postcode, notes, tags, total_jobs_count, total_leads_count, total_lifetime_value, last_contact_date, review_requests_opted_out, created_at, updated_at';

export function useCustomers(searchQuery?: string) {
  return useQuery({
    queryKey: ['customers', searchQuery],
    queryFn: async () => {
      let query = supabase
        .from('customers')
        .select(CUSTOMER_SELECT_COLUMNS)
        .order('created_at', { ascending: false });

      if (searchQuery && searchQuery.trim()) {
        const safe = sanitizeSearchQuery(searchQuery);
        if (safe.length > 0) {
          query = query
            .or(`customer_name.ilike.%${safe}%,primary_phone.ilike.%${safe}%,primary_email.ilike.%${safe}%`)
            .limit(50);
        }
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Customer[];
    },
    placeholderData: keepPreviousData,
  });
}

export function useCustomer(customerId: string | null) {
  return useQuery({
    queryKey: ['customer', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      if (!customerId) return null;

      const { data, error } = await supabase
        .from('customers')
        .select(CUSTOMER_SELECT_COLUMNS)
        .eq('id', customerId)
        .single();

      if (error) throw error;
      return data as Customer;
    },
  });
}

export function useCustomerLeads(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-leads', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      if (!customerId) return [];

      // 1. Get leads directly linked to customer
      const { data: directLeads, error: directError } = await supabase
        .from('leads')
        .select(`
          *,
          service_types (name),
          lead_sources (name),
          csr_profile:profiles!leads_assigned_csr_id_fkey (full_name),
          tech_profile:techs (name)
        `)
        .eq('customer_id', customerId);

      if (directError) throw directError;

      // 2. Get leads linked via jobs (orphaned leads)
      const { data: jobLinkedLeads, error: jobError } = await supabase
        .from('jobs')
        .select(`
          lead:leads!jobs_lead_id_fkey (
            *,
            service_types (name),
            lead_sources (name),
            csr_profile:profiles!leads_assigned_csr_id_fkey (full_name),
            tech_profile:techs (name)
          )
        `)
        .eq('customer_id', customerId)
        .not('lead_id', 'is', null);

      if (jobError) throw jobError;

      // 3. Combine and Deduplicate
      const allLeads = [...(directLeads || [])];
      const seenIds = new Set(allLeads.map(l => l.id));

      if (jobLinkedLeads) {
        jobLinkedLeads.forEach((job: any) => {
          if (job.lead && !seenIds.has(job.lead.id)) {
            allLeads.push(job.lead);
            seenIds.add(job.lead.id);
          }
        });
      }

      // 4. Sort by creation date (newest first)
      return allLeads.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
  });
}

export function useCustomerJobs(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-jobs', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      if (!customerId) return [];

      const { data, error } = await supabase
        .from('jobs')
        .select(`
          *,
          service_types (name),
          lead_sources (name),
          techs (name)
        `)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useCustomerCommunications(customerId: string | null) {
  return useQuery({
    queryKey: ['customer-communications', customerId],
    enabled: !!customerId,
    queryFn: async () => {
      if (!customerId) return [];

      const { data, error } = await supabase
        .from('customer_communications')
        .select(`
          *,
          creator:profiles!customer_communications_created_by_fkey (full_name)
        `)
        .eq('customer_id', customerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const { locale } = useCompanyLocale();

  return useMutation({
    mutationFn: async (customerData: {
      customer_name: string;
      primary_phone: string;
      primary_email?: string | null;
      primary_address: string;
      customer_type?: string;
      company_name?: string | null;
    }) => {
      const { user, tenantId: companyId } = await requireTenantUser();

      // Use get_or_create_customer RPC to prevent duplicates
      const { data: customerId, error } = await supabase.rpc('get_or_create_customer', {
        _customer_name: customerData.customer_name,
        // E.164 so phone_last9 matching works in every market (NZ especially)
        _customer_phone: normalizePhoneE164(customerData.primary_phone, locale),
        _customer_email: customerData.primary_email || null,
        _customer_address: customerData.primary_address,
        _customer_type: customerData.customer_type || 'individual',
        _company_name: customerData.company_name || null,
        _company_id: companyId,
        _created_by: user.id,
      });

      if (error) throw error;

      // Fetch the customer record to return
      const { data: customer, error: fetchError } = await supabase
        .from('customers')
        .select(CUSTOMER_SELECT_COLUMNS)
        .eq('id', customerId)
        .single();

      if (fetchError) throw fetchError;
      return customer as Customer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      toast.success('Customer saved successfully');
    },
    onError: (error: any) => {
      if (error.message?.includes('unique') || error.code === '23505') {
        toast.error('A customer with this phone or email already exists');
      } else {
        toast.error(error.message || 'Failed to create customer');
      }
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const { locale } = useCompanyLocale();

  return useMutation({
    mutationFn: async ({ id, ...updates }: any) => {
      for (const key of ['primary_phone', 'secondary_phone'] as const) {
        if (typeof updates[key] === 'string' && updates[key]) {
          updates[key] = normalizePhoneE164(updates[key], locale);
        }
      }
      const { data, error } = await supabase
        .from('customers')
        .update(updates)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) {
        // Handle unique constraint violation
        if (error.code === '23505') {
          throw new Error('Another customer with this phone or email already exists');
        }
        // RLS WITH CHECK rejection
        if (error.code === '42501') {
          throw new Error("You don't have permission to edit this customer");
        }
        throw error;
      }
      // An UPDATE the RLS USING clause filters out returns zero rows with no
      // error (e.g. a CSR editing a customer they aren't assigned to). With
      // `.single()` that surfaced as a cryptic "JSON object requested" toast.
      if (!data) {
        throw new Error("You don't have permission to edit this customer, or it no longer exists");
      }
      return data;
    },
    onSuccess: async (_, variables) => {
      toast.success('Customer updated successfully');
      // Awaited, so the mutation stays pending until the saved row is back in
      // the cache. Callers that re-render a form on success would otherwise
      // read the stale pre-save row and appear to discard what was just saved.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['customers'] }),
        queryClient.invalidateQueries({ queryKey: ['customer', variables.id] }),
      ]);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update customer');
    },
  });
}

export function useCreateCustomerCommunication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (communicationData: any) => {
      const user = await requireSessionUser();

      const { data, error } = await supabase
        .from('customer_communications')
        .insert([{ ...communicationData, created_by: user.id }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-communications', data.customer_id] });
      toast.success('Communication logged successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to log communication');
    },
  });
}

const phoneLast9 = (phone: string | null | undefined) => (phone ?? '').replace(/\D/g, '').slice(-9);

/**
 * The customer record a lead belongs to. Prefers the explicit leads.customer_id
 * link; older leads (before the 2026-09-08 phone guard) were never linked, so
 * fall back to the customer owning the lead's phone number (last 9 digits,
 * primary or secondary). Returns null when nothing matches.
 */
export function useLinkedCustomer(lead: { customer_id?: string | null; customer_phone?: string | null } | null | undefined) {
  const customerId = lead?.customer_id ?? null;
  const key = customerId ? '' : phoneLast9(lead?.customer_phone);

  return useQuery({
    queryKey: ['linked-customer', customerId, key],
    enabled: !!customerId || key.length >= 8,
    staleTime: 30_000,
    queryFn: async (): Promise<Customer | null> => {
      let query = supabase.from('customers').select(CUSTOMER_SELECT_COLUMNS);
      query = customerId
        ? query.eq('id', customerId)
        : query.or(`phone_last9.eq.${key},secondary_phone_last9.eq.${key}`).order('created_at', { ascending: true });
      const { data, error } = await query.limit(1).maybeSingle();
      if (error) throw error;
      return (data as Customer | null) ?? null;
    },
  });
}

/**
 * Batch version of {@link useLinkedCustomer} for lists: one query resolves a
 * customer id for every row that has a phone but no customer_id, so the name
 * still links on rows created before the contact-sync triggers went live.
 *
 * Returns a lookup function, so callers can resolve per row while rendering.
 */
export function useCustomerIdsByPhone(
  rows: ReadonlyArray<{ customer_id?: string | null; customer_phone?: string | null }> | undefined,
) {
  // Cap the key set: a report can list thousands of jobs, and the lookup is a
  // convenience, not a correctness requirement.
  const keys = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows ?? []) {
      if (row.customer_id) continue;
      const key = phoneLast9(row.customer_phone);
      if (key.length >= 8) set.add(key);
      if (set.size >= 200) break;
    }
    return Array.from(set).sort();
  }, [rows]);

  const { data } = useQuery({
    queryKey: ['customer-ids-by-phone', keys],
    enabled: keys.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      // phoneLast9 strips everything but digits, so the list is safe to inline.
      const list = keys.join(',');
      const { data, error } = await supabase
        .from('customers')
        .select('id, phone_last9, secondary_phone_last9')
        .or(`phone_last9.in.(${list}),secondary_phone_last9.in.(${list})`)
        .order('created_at', { ascending: true });
      if (error) throw error;

      const map: Record<string, string> = {};
      for (const row of data ?? []) {
        // Oldest match wins, same tie-break as useLinkedCustomer.
        if (row.phone_last9 && !map[row.phone_last9]) map[row.phone_last9] = row.id;
        if (row.secondary_phone_last9 && !map[row.secondary_phone_last9]) {
          map[row.secondary_phone_last9] = row.id;
        }
      }
      return map;
    },
  });

  return useCallback(
    (row: { customer_id?: string | null; customer_phone?: string | null } | null | undefined) => {
      if (!row) return null;
      if (row.customer_id) return row.customer_id;
      const key = phoneLast9(row.customer_phone);
      return (key.length >= 8 && data?.[key]) || null;
    },
    [data],
  );
}
